import { create } from 'zustand';
import type { Location as ApiLocation } from '../api/locationApi';

/**
 * The slice of a location the chrome needs (switcher label + scoping id),
 * declared as a narrowing of the API type rather than re-typed by hand.
 * `address` is the switcher's optional secondary line; the locations list
 * endpoint models addresses as `addressLine1`/`city`/…, so this is only
 * ever populated by a caller that composes one.
 */
export type Location = Pick<ApiLocation, 'id' | 'name'> & { address?: string };

/**
 * What the user asked for. `system` means "follow the OS", which is a
 * PREFERENCE, not a colour — it has to be resolved before anything is drawn.
 */
export type ThemePreference = 'light' | 'dark' | 'system';

/** What is actually painted. `system` never reaches the DOM. */
export type ResolvedTheme = 'light' | 'dark';

/**
 * Kept as an alias because six auth pages and the top bar import `Theme` to
 * type a `theme === 'dark'` check. Those now read `resolvedTheme`, which is
 * this narrower type.
 */
export type Theme = ResolvedTheme;

const THEME_STORAGE_KEY = 'dashboard-theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * THEME OWNERSHIP.
 *
 * This store is the ONLY writer of the theme onto <html>. It did not use to
 * be: `ThemeProvider.applyMode` also wrote `data-theme` from the org's
 * branding mode, and for `system` it REMOVED the attribute — which renders
 * light whatever the OS is set to, because the dark tokens key off `.dark`
 * and `[data-theme="dark"]` and NOTHING in the theme keys off
 * `prefers-color-scheme`. Two writers, one attribute, and the branch that
 * looked like "follow the system" silently meant "force light".
 *
 * So `system` is resolved HERE, against the media query, and only ever a
 * concrete light/dark is written. ThemeProvider now hands its org default to
 * `setThemeDefault` instead of touching the DOM.
 */
function systemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.(DARK_QUERY)?.matches ?? false;
}

function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return preference;
}

/** The stored preference, or null when the user has never chosen one. */
function readStoredPreference(): ThemePreference | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
    return null;
  } catch {
    return null;
  }
}

function applyResolved(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  // Always a concrete value. Removing the attribute (the old `system` branch)
  // leaves the light tokens in force regardless of the OS setting.
  root.dataset.theme = resolved;
}

function persist(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // ignore
  }
}

/**
 * SELECTED LOCATION PERSISTENCE.
 *
 * Only the location ID is remembered, namespaced by org so a shared browser
 * never carries a location across tenants. `LocationSwitcher` restores it
 * AFTER the org's location list arrives and validates membership — a
 * revoked or deleted location falls back to the first one. Before this,
 * every reload silently reset a manager working at location B to location
 * A, and every location-scoped read AND write on the next page (cash
 * drops, waste, counts, shifts) targeted the wrong location.
 *
 * Every read/write is wrapped: private-mode Safari and storage-denied
 * environments throw, and a location picker must not.
 */
const REMEMBERED_LOCATION_KEY = 'dashboard-selected-location';

function rememberedLocationKey(orgId: string): string {
  return `${REMEMBERED_LOCATION_KEY}.${orgId}`;
}

export function readRememberedLocationId(orgId: string | null | undefined): string | null {
  if (!orgId || typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(rememberedLocationKey(orgId));
  } catch {
    return null;
  }
}

export function rememberLocationId(orgId: string, locationId: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (locationId) window.localStorage.setItem(rememberedLocationKey(orgId), locationId);
    else window.localStorage.removeItem(rememberedLocationKey(orgId));
  } catch {
    // ignore
  }
}

/** Forget every org's remembered location — called from `authStore.logout`. */
export function clearRememberedLocations(): void {
  if (typeof window === 'undefined') return;
  try {
    const stale: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(`${REMEMBERED_LOCATION_KEY}.`)) stale.push(key);
    }
    stale.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // ignore
  }
}

interface UiState {
  /** What the user asked for — may be 'system'. */
  theme: ThemePreference;
  /** What is painted right now — never 'system'. */
  resolvedTheme: ResolvedTheme;
  /** True until the user makes an explicit choice; org branding may set the default. */
  themeIsDefault: boolean;

  activeWorkspace: string | null;
  workspaceLauncherOpen: boolean;
  selectedLocation: Location | null;
  locations: Location[];
  /**
   * The org whose locations are loaded. Namespaces the remembered
   * selection; set by `LocationSwitcher` (the one fetcher of the list).
   * Lives here rather than being read from authStore because uiStore is
   * imported by lib/axios, which authStore imports — an import back
   * would close a cycle.
   */
  locationsOrgId: string | null;

  setTheme: (theme: ThemePreference) => void;
  /** Org branding's mode. Applied ONLY while the user has made no choice. */
  setThemeDefault: (theme: ThemePreference) => void;
  toggleTheme: () => void;
  setActiveWorkspace: (workspace: string | null) => void;
  toggleWorkspaceLauncher: () => void;
  setWorkspaceLauncherOpen: (open: boolean) => void;
  /** Also remembers the id for the current `locationsOrgId` (see above). */
  setSelectedLocation: (location: Location | null) => void;
  /** Pass `orgId` when the list is the org's fetched roster; omit to keep the scope. */
  setLocations: (locations: Location[], orgId?: string | null) => void;
}

const storedPreference = readStoredPreference();
const initialPreference: ThemePreference = storedPreference ?? 'system';
const initialResolved = resolveTheme(initialPreference);
applyResolved(initialResolved);

export const useUiStore = create<UiState>((set, get) => ({
  theme: initialPreference,
  resolvedTheme: initialResolved,
  themeIsDefault: storedPreference === null,

  activeWorkspace: null,
  workspaceLauncherOpen: false,
  selectedLocation: null,
  locations: [],
  locationsOrgId: null,

  setTheme: (theme) => {
    const resolved = resolveTheme(theme);
    applyResolved(resolved);
    persist(theme);
    set({ theme, resolvedTheme: resolved, themeIsDefault: false });
  },

  setThemeDefault: (theme) => {
    // An explicit choice outranks the org default; without this guard a
    // branding refetch would stomp the user's selection on every mount.
    if (!get().themeIsDefault) return;
    const resolved = resolveTheme(theme);
    applyResolved(resolved);
    set({ theme, resolvedTheme: resolved });
  },

  toggleTheme: () => {
    // Flips what is CURRENTLY PAINTED, which is what a toggle means when the
    // preference is 'system' — and makes the result explicit, because a
    // toggle the OS can silently undo is not a toggle.
    const next: ThemePreference = get().resolvedTheme === 'dark' ? 'light' : 'dark';
    get().setTheme(next);
  },

  setActiveWorkspace: (workspace) => set({ activeWorkspace: workspace }),
  toggleWorkspaceLauncher: () =>
    set((s) => ({ workspaceLauncherOpen: !s.workspaceLauncherOpen })),
  setWorkspaceLauncherOpen: (open) => set({ workspaceLauncherOpen: open }),
  setSelectedLocation: (location) => {
    const orgId = get().locationsOrgId;
    if (orgId) rememberLocationId(orgId, location?.id ?? null);
    set({ selectedLocation: location });
  },
  setLocations: (locations, orgId) =>
    set(orgId === undefined ? { locations } : { locations, locationsOrgId: orgId }),
}));

// Follow the OS while — and only while — the preference is 'system'. Without
// this the setting would be read once at boot and then be wrong for the rest
// of the session every time the OS flipped at sunset.
if (typeof window !== 'undefined' && window.matchMedia) {
  const mql = window.matchMedia(DARK_QUERY);
  const onChange = () => {
    if (useUiStore.getState().theme !== 'system') return;
    const resolved = systemPrefersDark() ? 'dark' : 'light';
    applyResolved(resolved);
    useUiStore.setState({ resolvedTheme: resolved });
  };
  mql.addEventListener?.('change', onChange);
}
